import { useEffect, useState } from "react";
import api from "../../api/api";

type LeavePolicyForm = {
  deduction: boolean;
  carryForward: boolean;
  daysAllowable: number;
  hourly: boolean;
  monthly: boolean;
  sunday: boolean;
  saturday: boolean;
  lateDeduction: boolean;
  overtimeDeduction: boolean;
  rateofOvertime: number;
};

const DEFAULT_FORM: LeavePolicyForm = {
  deduction: false,
  carryForward: false,
  daysAllowable: 0,
  hourly: false,
  monthly: true,
  sunday: true,
  saturday: false,
  lateDeduction: false,
  overtimeDeduction: false,
  rateofOvertime: 1.0,
};

const LeavePolicySettings = () => {
  const [form, setForm] = useState<LeavePolicyForm>(DEFAULT_FORM);
  // Holidays now live on the Leave Management page, but the backend save
  // endpoint replaces the whole settings document — preserve the existing
  // holiday_dates so we don't wipe them when only the policy changes.
  const [holidayDates, setHolidayDates] = useState<any[]>([]);
  const [allowance, setAllowance] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const clearMessage = () => {
    setMessage("");
    setIsSuccess(false);
  };

  const fetchSettings = async () => {
    try {
      setIsInitialLoading(true);
      const { data } = await api.get("/salary/get_salary_settings");
      if (!data?.success) return;
      const d = data.data;
      setForm({
        deduction: d.deduction ?? false,
        carryForward: d.carryForward ?? false,
        daysAllowable: d.daysAllowable ?? 0,
        hourly: d.hourly ?? false,
        monthly: d.monthly ?? true,
        sunday: d.sunday ?? false,
        saturday: d.saturday ?? false,
        lateDeduction: d.lateDeduction ?? false,
        overtimeDeduction: d.overtimeDeduction ?? false,
        rateofOvertime: d.rateofOvertime ?? 1.0,
      });
      setHolidayDates(d.holiday_dates ?? []);
      setAllowance(d.allowance ?? false);
    } catch (err) {
      console.error("Failed to fetch leave policy", err);
    } finally {
      setIsInitialLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleHourlyMonthlyChange = (checked: boolean, isHourly: boolean) => {
    setForm((prev) => ({
      ...prev,
      hourly: isHourly ? checked : false,
      monthly: isHourly ? false : checked,
    }));
  };

  const handleDeductionsChange = (checked: boolean, isDeduction: boolean) => {
    setForm((prev) => ({
      ...prev,
      deduction: isDeduction ? checked : false,
      carryForward: isDeduction ? false : checked,
    }));
  };

  const handleLateDeductionsChange = (checked: boolean, isLateDeduction: boolean) => {
    setForm((prev) => ({
      ...prev,
      lateDeduction: isLateDeduction ? checked : false,
      overtimeDeduction: isLateDeduction ? false : checked,
      rateofOvertime:
        !isLateDeduction && checked ? prev.rateofOvertime || 0 : 0,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setMessage("");
    try {
      await api.post("/salary/create_or_update_salary_settings", {
        ...form,
        allowance,
        holiday_dates: holidayDates,
      });
      setMessage("Successfully saved leave policy!");
      setIsSuccess(true);
      fetchSettings();
    } catch (err: any) {
      const detail = err?.response?.data?.detail || "Failed to save leave policy";
      setMessage(detail);
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (isInitialLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="gt-eyebrow-row">
          <span className="gt-leaf" />
          <span className="gt-eyebrow">Company · iv. Leave Policy</span>
        </div>
        <h2 className="text-2xl font-semibold mt-2">
          Leave <em className="gt-serif-italic">policy</em>
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Configure deductions, working days, and overtime rules used by payroll.
        </p>
      </div>

      {/* Leave Policy */}
      <div className="p-6 border border-green-200 rounded-2xl bg-green-50 shadow-sm">
        <h3 className="text-lg font-bold mb-4 text-green-800">Policy</h3>
        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.deduction}
              onChange={(e) => handleDeductionsChange(e.target.checked, true)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Deduction</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.carryForward}
              onChange={(e) => handleDeductionsChange(e.target.checked, false)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Carry Forward</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.hourly}
              onChange={(e) => handleHourlyMonthlyChange(e.target.checked, true)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Hourly</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.monthly}
              onChange={(e) => handleHourlyMonthlyChange(e.target.checked, false)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Monthly</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.sunday}
              onChange={(e) => setForm({ ...form, sunday: e.target.checked })}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Sunday</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.saturday}
              onChange={(e) => setForm({ ...form, saturday: e.target.checked })}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Saturday</span>
          </label>

          <label className="flex items-center gap-2 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md hover:border-green-300 transition-all cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.lateDeduction}
              onChange={(e) => handleLateDeductionsChange(e.target.checked, true)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Late Deduction</span>
          </label>

          <label className="flex items-center gap-3 p-3 border border-green-200 rounded-xl bg-white hover:shadow-md transition-all text-sm">
            <input
              type="checkbox"
              checked={form.overtimeDeduction}
              onChange={(e) => handleLateDeductionsChange(e.target.checked, false)}
              className="w-4 h-4 text-green-600 rounded focus:ring-green-400"
            />
            <span className="font-semibold text-gray-800">Overtime Deduction</span>
            {form.overtimeDeduction && (
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-gray-500">Rate</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.rateofOvertime}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      rateofOvertime:
                        e.target.value === "" ? 0 : parseFloat(e.target.value),
                    })
                  }
                  placeholder="1.5"
                  className="w-20 px-2 py-1 border rounded-md text-sm focus:ring-2 focus:ring-green-400"
                />
              </div>
            )}
          </label>
        </div>
      </div>

      {/* Days Allowable */}
      <div>
        <label className="block text-lg font-bold mb-3 text-gray-800">
          Days Allowable
        </label>
        <input
          type="number"
          className="w-full p-4 border-2 border-indigo-200 rounded-2xl focus:ring-4 focus:ring-indigo-100 focus:border-indigo-500 bg-indigo-50 shadow-sm text-xl font-bold text-center"
          value={form.daysAllowable}
          onChange={(e) =>
            setForm({ ...form, daysAllowable: Number(e.target.value) || 0 })
          }
          min={0}
        />
      </div>

      {message && (
        <div
          className={`p-4 rounded-xl shadow-sm text-base font-semibold flex items-center justify-between ${
            isSuccess
              ? "bg-green-100 border-2 border-green-300 text-green-800"
              : "bg-red-100 border-2 border-red-300 text-red-800"
          }`}
        >
          <span>{message}</span>
          <button onClick={clearMessage} className="ml-3 text-lg hover:text-black">
            ×
          </button>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={isLoading}
          className="gt-btn is-primary is-lg"
        >
          {isLoading ? "Saving..." : "Save Leave Policy"}
        </button>
      </div>
    </div>
  );
};

export default LeavePolicySettings;
